import React, { useEffect, useState } from "react"
import ClientReportCard from "../../components/ClientReportCard.molecule"
import useScreenDimensionsContext from "../../../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import { ValueWithLabelTemplate } from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import { numberFormatter } from "../../../../utils/numbers.utils"
import { ReactComponent as HeapSnapshotThumbnailIcon } from "../../../../design/assests/images/icons/heap_snapshot_thumbnail.svg"
import {
  getClientSprintPointProgressBySprintId,
  getBudgetProjectionEstimation,
} from "../../../../services/clientReport.service"
import GeneralSprintPointProgressChart from "../../components/GeneralSprintPointProgressChart.compound"
import {
  formatPointProgressDoughnutChartData,
  getEstimatedEndDateToCompletePoints,
  getDevelopmentCompletionEstimate,
  convertWeekEstimnatedtoDateEstimated,
  getProyectionInBlocksByWeeks,
} from "../../../../utils/clientReport.utils"
import { formatOnlyMonth } from "../../../../utils/date.utils"
import {
  GENERAL_SPRINT_PROGRESS_CHART_INITIAL_STATE,
  PERIOD_LABELS,
} from "../../../../consts/modules/clientReport.consts"

export const GENERAL_SPRINT_PROGRESS_CARD_MODES = {
  COMPACT: "compact",
  LONG: "long",
}

const GeneralSprintProgress = ({
  refresh,
  sprintData,
  setEstimatedPointsFinishDate = () => {},
  sizeMode = GENERAL_SPRINT_PROGRESS_CARD_MODES.LONG,
  refChart,
  period,
  isPresentationMode = false,
  collapsible = false,
  clientId,
  extraData,
}) => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({})
  const [chartData, setChartData] = useState(
    GENERAL_SPRINT_PROGRESS_CHART_INITIAL_STATE
  )
  const { screenWidth } = useScreenDimensionsContext()
  const isMobile = screenWidth < 985

  const isDataEnough = data?.totalCompletedPoints && data?.totalPointsToComplete
  const [estimate, setEstimate] = useState(null)
  const [budgetProjection, setBudgetProjection] = useState([])
  const lastProjection =
    budgetProjection?.length ?
      budgetProjection[budgetProjection.length - 1]
    : null

  const projectionRange =
    lastProjection ?
      {
        min: lastProjection.pessimisticProjectionWeeks,
        max: lastProjection.optimisticProjectionWeeks,
      }
    : null
  const pessimisticProjection =
    extraData?.semanasPesimistas ??
    (isPresentationMode ?
      sprintData?.semanasPesimistas
    : lastProjection?.pessimisticProjectionWeeks) ??
    null

  const optimisticProjection =
    extraData?.semanasOptimistas ??
    (isPresentationMode ?
      sprintData?.semanasOptimistas
    : lastProjection?.optimisticProjectionWeeks) ??
    null

  const nominalProjection =
    extraData?.semanasNominales ??
    (isPresentationMode ?
      sprintData?.semanasNominales
    : lastProjection?.nominalProjectionWeeks) ??
    null

  const endDateReference =
    extraData?.blockEndDate ??
    (isPresentationMode ? sprintData?.blockEndDate : lastProjection?.endDate) ??
    null

  const nominalProjectionInDate =
    nominalProjection && endDateReference ?
      convertWeekEstimnatedtoDateEstimated(
        endDateReference,
        nominalProjection,
        extraData?.holidays ?? sprintData?.holidays ?? []
      )
    : null

  const nominalProjectionFormatted =
    nominalProjectionInDate ? formatOnlyMonth(nominalProjectionInDate) : null

  const averagePoints =
    extraData?.averagePointsPerBlock != null ? extraData.averagePointsPerBlock
    : isPresentationMode ? sprintData?.averagePointsPerBlock || null
    : data?.averagePointsPerSprint || null

  let sprintId

  if (extraData?.sprintId) {
    sprintId = extraData.sprintId
  } else if (isPresentationMode) {
    sprintId = sprintData?.sprintId
  } else {
    sprintId = sprintData?.id
  }

  useEffect(() => {
    fetchSprintProgressData()
  }, [sprintId, refresh])

  const fetchSprintProgressData = async () => {
    setLoading(true)

    try {
      if (!sprintId) {
        setData({})
        setChartData(GENERAL_SPRINT_PROGRESS_CHART_INITIAL_STATE)
        setBudgetProjection([])
        return setLoading(false)
      }

      const budgetResponse = await getBudgetProjectionEstimation(
        sprintData.clientId,
        period.toUpperCase(),
        sprintId
      )

      if (budgetResponse.success) {
        setBudgetProjection(budgetResponse.result)
      } else {
        setBudgetProjection([])
      }
      // EXISTENTE

      const response = await getClientSprintPointProgressBySprintId(sprintId)

      if (!response.success) {
        throw new Error(response.message)
      }

      const result = response.result
      const chartResult =
        extraData?.periodPoints != null ?
          {
            ...result,
            totalCompletedPoints: Number(extraData.periodPoints),
          }
        : result
      setData(result)

      setChartData(formatPointProgressDoughnutChartData(chartResult))
      setEstimatedPointsFinishDate(
        getEstimatedEndDateToCompletePoints(chartResult)
      )

      const estimate = getDevelopmentCompletionEstimate(result)
      setEstimate(estimate)
    } catch (error) {
      console.error("Error fetching sprint progress data:", error)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    if (!data?.totalPointsToComplete) return

    const chartResult =
      extraData?.periodPoints != null ?
        {
          ...data,
        }
      : data

    setChartData(formatPointProgressDoughnutChartData(chartResult))
  }, [extraData, data])
  return (
    <ClientReportCard
      collapsible={collapsible}
      key={sprintId}
      title={"Progreso del alcance"}
      icon={<HeapSnapshotThumbnailIcon />}
      className="font-cairo flex-shrink-0"
    >
      <div className="flex flex-wrap gap-3 w-full align-items-center">
        <div className="grid-column grid-column--rest">
          <div ref={refChart}>
            <GeneralSprintPointProgressChart
              data={chartData}
              middleLabel
              middleLabelValue={`${Math.floor(
                (Number(data?.totalCompletedPoints) * 100) /
                  data?.totalPointsToComplete
              )}%`}
              showEmptyMessage={!isDataEnough}
              loading={loading}
              sizeMode={sizeMode}
            />
          </div>
        </div>

        <div className="grid-column grid-column--rest">
          <div className="flex flex-column h-full">
            <ValueWithLabelTemplate
              label="Avance en puntos"
              labelTooltip
              labelTooltipContent="Puntos acumulados en comparación con los puntos planificados"
              labelTooltipIcon="pi pi-info-circle"
              labelTooltipPosition={isMobile ? "left" : "right"}
              value={
                isDataEnough ?
                  <div className="flex align-items-center gap-2">
                    <span className="value-with-unit">
                      {numberFormatter(Number(data?.totalCompletedPoints))}
                      <span className="unit">
                        /{numberFormatter(data?.totalPointsToComplete)} pts dev
                      </span>
                    </span>
                  </div>
                : "-"
              }
              loading={loading}
            />

            <ValueWithLabelTemplate
              label="Proyección de bloques restantes"
              labelTooltip
              labelTooltipContent="Proyección estimada del alcance según la velocidad actual"
              labelTooltipIcon="pi pi-info-circle"
              labelTooltipPosition={isMobile ? "left" : "right"}
              value={
                (
                  !isDataEnough ||
                  optimisticProjection == null ||
                  pessimisticProjection == null
                ) ?
                  "-"
                : <div className="flex align-items-center gap-2">
                    <span className="value-with-unit">
                      ~{" "}
                      {`${getProyectionInBlocksByWeeks({ weeks: optimisticProjection })} - ${getProyectionInBlocksByWeeks({ weeks: pessimisticProjection })}`}
                      <span className="unit"> bloques</span>
                    </span>
                  </div>
              }
              loading={loading}
            />
          </div>
        </div>

        <div className="grid-column grid-column--rest">
          <div className="flex flex-column  h-full">
            <ValueWithLabelTemplate
              label="Velocidad promedio"
              labelTooltip
              labelTooltipContent="Calculado según los últimos 8 sprints"
              labelTooltipIcon="pi pi-info-circle"
              labelTooltipPosition={isMobile ? "left" : "right"}
              value={
                !isDataEnough || averagePoints == null || averagePoints === "" ?
                  "-"
                : <div className="flex align-items-center gap-2">
                    <span className="value-with-unit">
                      {numberFormatter(averagePoints)}
                      <span className="unit">
                        {" "}
                        pts dev/{PERIOD_LABELS[period] ?? period}
                      </span>
                    </span>
                  </div>
              }
              loading={loading}
            />

            <ValueWithLabelTemplate
              label="Fin del alcance estimado"
              labelTooltip
              labelTooltipContent="Tiempo estimado para realizar los puntos planificados según la velocidad promedio actual"
              labelTooltipIcon="pi pi-info-circle"
              labelTooltipPosition={isMobile ? "left" : "right"}
              value={
                <div className="flex align-items-center gap-2">
                  <span className="value-with-unit">
                    ~ {nominalProjectionFormatted || "-"}
                  </span>
                </div>
              }
              loading={loading}
            />
          </div>
        </div>
      </div>
    </ClientReportCard>
  )
}

export default GeneralSprintProgress
